import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import userSelectors from '../store/selectors/user.selectors'
import { NOTIFICATION_TYPES } from '../constants/notification-types'
import baseApi from '../store/apis/base.api'
import { useLazyFetchNewNotificationsQuery } from '../store/apis/notification.api'
import {
    getActionsFromType,
    getMessageFromType,
    getNotificationType,
    openNotificationWithIcon,
} from '../utils/notification.helper'

const POLL_INTERVAL_MS = 5000

const mergeNewNotifications = (draft, newNotifications) => {
    if (!draft?.notifications) {
        return
    }

    const existingById = new Map(draft.notifications.map(notification => [notification.id, notification]))
    let addedUnread = 0

    for (const incoming of [...newNotifications].reverse()) {
        const existing = existingById.get(incoming.id)
        if (existing) {
            Object.assign(existing, incoming)
            continue
        }
        draft.notifications.unshift(incoming)
        if (!incoming.read) {
            addedUnread += 1
        }
    }

    draft.totalUnread = (draft.totalUnread ?? 0) + addedUnread
}

const showNotificationToasts = newNotifications => {
    newNotifications.forEach(notification => {
        if (notification.type === NOTIFICATION_TYPES.ACTION) {
            openNotificationWithIcon(
                getNotificationType(notification.type),
                getMessageFromType(notification.payload?.action, notification.payload),
                undefined,
                undefined,
                getActionsFromType(notification.payload?.action, notification.payload, notification.url),
            )
            return
        }

        openNotificationWithIcon(
            getNotificationType(notification.type),
            notification.message,
            undefined,
            undefined,
            getActionsFromType(notification.payload?.action, notification.payload, notification.url),
        )
    })
}

const useNotificationPolling = () => {
    const userId = useSelector(userSelectors.id)
    const dispatch = useDispatch()
    const [fetchNewNotifications] = useLazyFetchNewNotificationsQuery()

    useEffect(() => {
        if (!userId) {
            return undefined
        }

        const poll = async () => {
            if (document.hidden) {
                return
            }

            try {
                const { notifications: newNotifications } = await fetchNewNotifications(
                    undefined,
                    false,
                ).unwrap()
                if (!newNotifications?.length) {
                    return
                }

                dispatch(
                    baseApi.util.updateQueryData('fetchNotifications', undefined, draft => {
                        mergeNewNotifications(draft, newNotifications)
                    }),
                )
                showNotificationToasts(newNotifications)
            } catch {
                // Keep polling even if a single request fails.
            }
        }

        poll()
        const intervalId = setInterval(poll, POLL_INTERVAL_MS)
        return () => clearInterval(intervalId)
    }, [userId, dispatch, fetchNewNotifications])
}

export default useNotificationPolling
